import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import File from "../../assets/icon/svgs/File.svg?react";
import TrashBind from "../../assets/icon/svgs/TrashBind.svg?react";
import { Loader } from "../../assets/icon/loader";
import { useTranslation } from "react-i18next";
import { InputDialog } from "../customComponents/InputDialog";
import { useForm } from "react-hook-form";
import { SelectWithPlaceHolder } from "../customComponents/SelectWithPlaceHolder";
import { ProjectDescriptionEditor } from "../customComponents/ProjectDescriptionEditor";
import HouseManagerApi from "@/api-client/api/services/houseManager/houseManager";
import { useDropzone } from "react-dropzone";
import { cn } from "@/lib/utils";
import { PopUpDeleteProject } from "./PopUpDeleteProject";

interface ProjectFormData {
  title: string;
  description: string;
  status: string;
  new_files: File[];
}

interface Item {
  id: number;
  title: string;
  description: string;
  building: number;
  status: string;
  files: {
    id: number;
    pdf: string;
  }[];
}
export function PopUpAddAndEditProject({
  trigger,
  building_id,
  fetchTenantRelationships,
  item,
}: {
  trigger: React.ReactNode;
  building_id: number | null;
  fetchTenantRelationships: () => void;
  item?: Item;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [loader, setLoader] = useState(false);
  const [file, setFile] = useState<{ pdf: string; id: number }[]>(
    item?.files || []
  );
  const [fileForDeleteArray, setFileForDeleteArray] = useState<string[]>([]);

  const methods = useForm<ProjectFormData>({
    defaultValues: {
      title: item?.title || "",
      description: item?.description || "",
      status: item?.status || "",
      new_files: [],
    },
  });

  const {
    watch,
    setValue,
    getValues,
    // handleSubmit,
    // setError,
    // formState: { errors },
  } = methods;

  const handleDialogClose = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const onDrop = (acceptedFiles: File[]) => {
    const totalFiles =
      uploadedFiles.length + file.length + acceptedFiles.length;
    if (totalFiles > 5) {
      alert(t("popUpAddAndEditProject.max_files_warning"));
      return;
    }

    const updatedFiles = [...uploadedFiles, ...acceptedFiles];
    setUploadedFiles(updatedFiles);
    setValue("new_files", updatedFiles);
  };

  const removeFile = (file: File) => {
    const updatedFiles = uploadedFiles.filter((f) => f !== file);
    setUploadedFiles(updatedFiles);
    setValue("new_files", updatedFiles);
    console.log("updatedFiles", getValues("new_files"));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      "application/pdf": [],
    },
  });

  const postTenantRelationship = async () => {
    try {
      if (!building_id) return;
      const data = {
        title: getValues("title"),
        description: getValues("description"),
        status: getValues("status"),
        building: building_id,
        new_files: getValues("new_files"),
      };
      setLoader(true);
      console.log("new_files", getValues("new_files"));
      const response = await HouseManagerApi.postRepairWorks(data);
      fetchTenantRelationships();
      handleDialogClose(false);
      return response;
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const patchTenantRelationship = async () => {
    try {
      if (!building_id) return;
      fileForDeleteArray.map((file_id) => {
        deleteFile(file_id);
      });
      setLoader(true);
      const data = {
        title: getValues("title"),
        description: getValues("description"),
        status: getValues("status"),
        building: building_id,
        new_files: getValues("new_files"),
      };
      const response = await HouseManagerApi.patchRepairWorks(
        item?.id.toString() || "",
        data
      );
      fetchTenantRelationships();
      handleDialogClose(false);
      return response;
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const deleteFileFromateArray = (file_id: string) => {
    setFile(file.filter((f) => f.id !== parseInt(file_id)));
    setFileForDeleteArray((prev) => [...prev, file_id]);
  };

  const deleteFile = async (file_id: string) => {
    try {
      const response = await HouseManagerApi.deleteRepairWorksFile(
        item?.id.toString() || "",
        file_id
      );
      fetchTenantRelationships();
      return response;
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      methods.reset({
        title: item?.title || "",
        description: item?.description || "",
        status: item?.status || "",
        new_files: [],
      });
      setUploadedFiles([]);
      setFile(item?.files || []);
      setFileForDeleteArray([]);
    }
  }, [item, isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose} modal={false}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      {isOpen && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-[4px] z-40 pointer-events-none"></div>
      )}
      <DialogContent
        className="w-[80%] max-w-[1100px] max-h-full overflow-auto gap-4 pb-[40px]"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        {!loader ? (
          <>
            <DialogHeader className="mb-[16px]">
              <DialogTitle className="text-[24px] font-medium flex items-center">
                {item
                  ? t("popUpAddAndEditProject.edit_project")
                  : t("popUpAddAndEditProject.create_project_post")}
              </DialogTitle>
              <DialogDescription className="text-[15px] text-[#00000080] font-medium text-left">
                {!item &&
                  t("popUpAddAndEditProject.create_project_post_description")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <InputDialog
                    id="popUpAddAndEditProject.project_name"
                    validationError={emailError}
                    placeholder={t("popUpAddAndEditProject.project_name")}
                    isBiggerInput={true}
                    inputValue={watch("title")}
                    disablePlaceholderAnimation={false}
                    setValidationError={(error) => {
                      setEmailError(error ?? "");
                    }}
                    setInputValue={(value) => {
                      if (value.length > 200) {
                        return;
                      }
                      setValue("title", value);
                    }}
                  />
                  <span className="text-[15px] font-medium text-[#00000080] self-end">
                    {t("symbols")} {200 - watch("title").length}
                  </span>
                </div>

                <ProjectDescriptionEditor
                  placeholder={t("popUpAddAndEditProject.project_description")}
                  value={watch("description")}
                  maxLength={2000}
                  setValue={(val) => {
                    setValue("description", val);
                  }}
                />

                <SelectWithPlaceHolder
                  selectValue={watch("status") || item?.status || ""}
                  placeholder={t("popUpAddAndEditProject.select_status")}
                  onSelectChange={(val) => {
                    setValue("status", val);
                  }}
                  selectValues={[
                    { name: t("planned"), value: "planned" },
                    { name: t("in_progress"), value: "in_progress" },
                    { name: t("done"), value: "done" },
                    { name: t("canceled"), value: "canceled" },
                  ]}
                  manyChoices={false}
                />

                <div className="flex flex-col gap-1">
                  <div className="flex flex-col gap-2">
                    <div
                      {...getRootProps()}
                      className={cn(
                        "flex items-center justify-center px-4 py-8 border-2 border-dashed rounded-[10px] cursor-pointer transition-colors",
                        isDragActive
                          ? "border-[#0027CB] bg-blue-50"
                          : "border-neutral-300 bg-neutral-50"
                      )}
                    >
                      <input {...getInputProps()} />
                      <p className="text-neutral-500 text-center">
                        {isDragActive
                          ? t("drag_and_drop_active")
                          : t("drag_and_drop_file_or_click_to_upload")}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2">
                      {file.map((file) => (
                        <div className="flex flex-row justify-between p-2 border-b-[2px]">
                          <a
                            href={file.pdf}
                            className="text-[18px] font-medium w-fit flex flex-row gap-2"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <File width={24} height={24} />
                            {file.pdf?.split("/").pop()}
                          </a>
                          <Button
                            className="flex flex-row gap-1 text-[17px] text-[#00000080] px-0 leading-[130%] h-fit"
                            variant="link"
                            size="sm"
                            onClick={() =>
                              deleteFileFromateArray(file.id.toString())
                            }
                          >
                            {t("popUpAddAndEditProject.delete_a_file")}
                          </Button>
                        </div>
                      ))}
                      {uploadedFiles.length > 0 && (
                        <>
                          {uploadedFiles.map((file, idx) => (
                            <div className="flex flex-row justify-between p-2 border-b-[2px]">
                              <a
                                href={URL.createObjectURL(file)}
                                className="text-[18px] font-medium w-fit flex flex-row gap-2"
                                target="_blank"
                                rel="noopener noreferrer"
                                key={idx}
                              >
                                <File width={24} height={24} />
                                {file.name}
                              </a>
                              <Button
                                className="flex flex-row gap-1 text-[17px] text-[#00000080] px-0 leading-[130%] h-fit"
                                variant="link"
                                size="sm"
                                onClick={() => removeFile(file)}
                              >
                                {t("popUpAddAndEditProject.delete_a_file")}
                              </Button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center">
                {item ? (
                  <PopUpDeleteProject
                    trigger={
                      <Button
                        className="flex flex-row gap-1 text-[20px] text-[#00000080] px-0 leading-[130%] h-fit"
                        variant="link"
                        size="sm"
                      >
                        <TrashBind width={24} height={24} />
                        {t("popUpAddAndEditProject.delete_publication")}
                      </Button>
                    }
                    projectId={item?.id.toString() || ""}
                    fetchTenantRelationships={fetchTenantRelationships}
                  />
                ) : (
                  <div></div>
                )}
                <Button
                  type="submit"
                  variant="system"
                  size="system"
                  className="w-fit"
                  disabled={
                    !watch("title") || !watch("description") || !watch("status")
                  }
                  loading={false}
                  onClick={
                    item ? patchTenantRelationship : postTenantRelationship
                  }
                >
                  {item
                    ? t("popUpAddAndEditProject.save_and_republish")
                    : t("popUpAddAndEditProject.save_and_publish")}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-8 justify-center items-center h-full">
            <span className="text-[20px] font-medium text-[#00000080]">
              {t("popUpAddAndEditProject.loading")}
            </span>
            <Loader isGrey />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
